import { useCallback, useContext, useRef } from "react";
import getExperimentsContext from "./getExperimentsContext";
import { OptimizelyOptions } from "./types";
import { isShallowEqual } from "./utils/isShallowEqual";

type ExperimentHook<Variables = { [variableKey: string]: unknown }> = {
  getVariation: () => string | null;
  activate: () => void;
  track: (eventKey: string) => void;
  getExperimentVariables: () => Variables | null;
};
const useExperiment = <Variables = { [variableKey: string]: unknown }>(
  experimentId: string,
  options?: OptimizelyOptions,
): ExperimentHook<Variables> => {
  const ExperimentsContext = getExperimentsContext<Variables>();
  const optimizelyManager = useContext(ExperimentsContext);

  const optionsRef = useRef(options);
  if (!isShallowEqual(optionsRef.current, options)) {
    optionsRef.current = options;
  }

  const customUserAttributes = optionsRef.current?.customUserAttributes;

  const getVariation = useCallback(
    () => optimizelyManager.getVariation(experimentId, customUserAttributes),
    [experimentId, optimizelyManager, customUserAttributes],
  );
  const activate = useCallback(
    () => optimizelyManager.activate(experimentId, customUserAttributes),
    [experimentId, optimizelyManager, customUserAttributes],
  );
  const track = useCallback(
    (eventKey: string) =>
      optimizelyManager.track(eventKey, customUserAttributes),
    [optimizelyManager, customUserAttributes],
  );

  const getExperimentVariables = useCallback(
    () => optimizelyManager.getVariables(experimentId),
    [optimizelyManager, experimentId],
  );

  return { getVariation, activate, track, getExperimentVariables };
};

export default useExperiment;
